import type { PropertyFilters as FilterPanelFilters } from "../components/FilterPanel";
import { PROPERTY_FILTER_KEYS, qtyChipToStoreValue } from "@/shared/utils/property-filter-keys";

type AdvancedFilters = Record<string, unknown>;

/** @returns true when value is truthy. */
const isTruthy = (value: unknown): boolean => Boolean(value);
/** @returns true when value is a non-empty array. */
const isNonEmptyArray = (value: unknown): boolean => Array.isArray(value) && value.length > 0;
/** @returns numeric value after stripping non-digit characters. */
const digitsToNumber = (value: string): number => Number(value.replace(/\D/g, ''));
/**
 * @returns numeric value of a masked string, honouring the two decimal places
 * the money/area masks append (`"R$ 1.500,00"` → `1500`, `"44,00 m²"` → `44`).
 * Values without a decimal tail are read as whole units.
 */
const maskedToNumber = (value: string): number => {
  // Symbols and units are dropped first so the decimal tail is still the end of
  // the string for "44,00 m²" just as it is for "R$ 1.500,00".
  const numeric = value.replace(/[^\d.,]/g, '');
  const digits = Number(numeric.replace(/\D/g, ''));
  return /[.,]\d{2}$/.test(numeric) ? digits / 100 : digits;
};
/** @returns the value unchanged. */
const identity = (value: unknown): unknown => value;

interface PanelMapping {
  panelKey: keyof FilterPanelFilters;
  storeKey: string;
  isActive: (value: unknown) => boolean;
  transform: (value: any) => unknown;
}

const PANEL_MAPPINGS: PanelMapping[] = [
  { panelKey: 'state', storeKey: 'state', isActive: isTruthy, transform: identity },
  { panelKey: 'city', storeKey: 'city', isActive: isTruthy, transform: identity },
  { panelKey: 'neighborhood', storeKey: 'neighborhood', isActive: isTruthy, transform: identity },
  { panelKey: 'categories', storeKey: 'category', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'transactions', storeKey: 'transaction_type', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'priceMinSale', storeKey: 'sale_price_min', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'priceMaxSale', storeKey: 'sale_price_max', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'priceMinRent', storeKey: 'rent_price_min', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'priceMaxRent', storeKey: 'rent_price_max', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'bedrooms', storeKey: 'bedrooms', isActive: isTruthy, transform: qtyChipToStoreValue },
  { panelKey: 'bathrooms', storeKey: 'bathrooms', isActive: isTruthy, transform: qtyChipToStoreValue },
  { panelKey: 'parking', storeKey: 'parking_spots', isActive: isTruthy, transform: qtyChipToStoreValue },
  { panelKey: 'suites', storeKey: 'suites', isActive: isTruthy, transform: qtyChipToStoreValue },
  { panelKey: 'areaBuiltMin', storeKey: 'built_area_min', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'areaBuiltMax', storeKey: 'built_area_max', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'areaTotalMin', storeKey: 'area_min', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'areaTotalMax', storeKey: 'area_max', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'condoFeeMin', storeKey: 'condo_fee_min', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'condoFeeMax', storeKey: 'condo_fee_max', isActive: isTruthy, transform: maskedToNumber },
  { panelKey: 'condoPaymentFrequency', storeKey: 'condo_payment_frequency', isActive: isTruthy, transform: identity },
  { panelKey: 'petFriendly', storeKey: PROPERTY_FILTER_KEYS.acceptsPets, isActive: (v) => v === true, transform: identity },
  { panelKey: 'furnished', storeKey: 'furnished', isActive: isTruthy, transform: identity },
  { panelKey: 'hasVirtualTour', storeKey: 'has_virtual_tour', isActive: (v) => v === true, transform: identity },
  { panelKey: 'facing', storeKey: 'facing', isActive: isTruthy, transform: identity },
  { panelKey: 'exchange', storeKey: 'exchange', isActive: (v) => v === true, transform: identity },
  { panelKey: 'imported', storeKey: 'imported', isActive: (v) => v === true, transform: identity },
  { panelKey: 'yearBuiltMin', storeKey: 'year_built_min', isActive: isTruthy, transform: digitsToNumber },
  { panelKey: 'yearBuiltMax', storeKey: 'year_built_max', isActive: isTruthy, transform: digitsToNumber },
  { panelKey: 'floorMin', storeKey: 'floor_min', isActive: isTruthy, transform: digitsToNumber },
  { panelKey: 'floorMax', storeKey: 'floor_max', isActive: isTruthy, transform: digitsToNumber },
  { panelKey: 'totalRooms', storeKey: 'total_rooms', isActive: isTruthy, transform: qtyChipToStoreValue },
  { panelKey: 'amenitiesInternal', storeKey: 'amenities_internal', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'amenitiesExternal', storeKey: 'amenities_external', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'amenitiesSecurity', storeKey: 'amenities_security', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'condoLeisure', storeKey: 'condo_leisure', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'condoSecurity', storeKey: 'condo_security', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'condoExpenses', storeKey: 'condo_expenses', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'rentPaymentFrequency', storeKey: 'rent_payment_frequency', isActive: isTruthy, transform: identity },
  { panelKey: 'rentGuarantee', storeKey: 'rent_guarantee', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'rentIncludedExpenses', storeKey: 'rent_included_expenses', isActive: isNonEmptyArray, transform: identity },
  { panelKey: 'leaseTermMonths', storeKey: 'lease_term_months', isActive: isTruthy, transform: digitsToNumber },
  { panelKey: 'administrationType', storeKey: 'administration_type', isActive: isTruthy, transform: identity },
  { panelKey: 'parkingType', storeKey: 'parking_type', isActive: isNonEmptyArray, transform: identity },
];

/**
 * Maps the filter-panel input shape to the store's advanced-filter shape.
 * @returns The store-shaped advanced filters, omitting inactive fields.
 */
export function mapFilterPanelToStore(f: FilterPanelFilters): AdvancedFilters {
  return PANEL_MAPPINGS.reduce<AdvancedFilters>((store, mapping) => {
    const value = f[mapping.panelKey];
    if (mapping.isActive(value)) store[mapping.storeKey] = mapping.transform(value);
    return store;
  }, {});
}
